import { PropsWithChildren, useState } from 'react'
import { BaseModal } from 'components/organisms/modals/base-modal'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { PasswordMaskField } from 'components/common'
import { Button } from 'components/atoms/button'
import { passwordSchema } from 'utils/password'

interface Props {
	open: boolean
	setOpen?: (...args: any) => any
	handleOnSubmit?: (...args: any) => any
}

const validationSchema = Yup.object().shape({
	password: passwordSchema,
	passwordConfirmation: Yup.string()
		.required('Confirm password is required')
		.oneOf([Yup.ref('password')], 'Your confirm password does not match with new password. Please try again.'),
})

export function ChangePasswordModal({ open, setOpen, handleOnSubmit }: PropsWithChildren<Props>) {
	const [submitting, setSubmitting] = useState(false)
	const {
		control,
		handleSubmit,
		formState: { errors, isValid },
	} = useForm({
		resolver: yupResolver(validationSchema),
		mode: 'onChange',
	})

	const onSubmit = (values: any) => {
		setSubmitting(true)
		handleOnSubmit?.(values, setSubmitting)
	}

	return (
		<BaseModal open={open} setOpen={setOpen} title="Change Password" size="sm" className="p-8 pb-10">
			<div className="w-full text-left">
				<form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
					<input autoComplete="off" name="hidden" type="text" className="hidden" />
					<div className="space-y-4">
						<div>
							<Controller
								control={control}
								name="password"
								render={({ field }) => (
									<PasswordMaskField
										id="password"
										className="px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
										placeholder="Password"
										value={field.value ?? ''}
										onChange={field.onChange}
										onBlur={field.onBlur}
									/>
								)}
							/>
							{errors.password && (
								<div className="text-secondary-red mt-2 text-sm">
									{errors.password.message as string}
								</div>
							)}
						</div>
						<div>
							<Controller
								control={control}
								name="passwordConfirmation"
								render={({ field }) => (
									<PasswordMaskField
										id="passwordConfirmation"
										className="px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
										placeholder="Confirm password"
										value={field.value ?? ''}
										onChange={field.onChange}
										onBlur={field.onBlur}
									/>
								)}
							/>
							{errors.passwordConfirmation && (
								<div className="text-secondary-red mt-2 text-sm">
									{errors.passwordConfirmation.message as string}
								</div>
							)}
						</div>
					</div>
					<div className="mt-6">
						<Button
							variant="primary"
							fullWidth
							size="lg"
							type="submit"
							className="flex items-center justify-center gap-1"
							disabled={!isValid || submitting}
						>
							{submitting && <FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin />}
							Continue
						</Button>
					</div>
				</form>
			</div>
		</BaseModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface ChangePasswordModalManagedProps {
	handleOnSubmit?: (...args: any) => any
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'change-password': ChangePasswordModalManagedProps
	}
}

export function ManagedChangePasswordModal({
	onClose,
	...rest
}: ChangePasswordModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<ChangePasswordModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			{...rest}
		/>
	)
}
